import React, { useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Shield, FileText, ChevronDown, Eye, Globe, Users, Database, UserCheck, Clock, AlertTriangle, BookOpen, Mail, Download } from 'lucide-react';
interface PrivacySection {
  id: string;
  title: string;
  icon: React.ElementType;
  content: React.ReactNode;
}
const PRIVACY_SECTIONS: PrivacySection[] = [{
  id: 'informacion',
  title: '1. Información que Recopilamos',
  icon: FileText,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5653">
        <p data-id="element-5654">
          Recopilamos diferentes tipos de información para proporcionar y
          mejorar nuestro Servicio:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-5655">
          <li data-id="element-5656">
            <strong data-id="element-5657">Datos de cuenta:</strong> Nombre, dirección de correo
            electrónico y detalles de la organización.
          </li>
          <li data-id="element-5658">
            <strong data-id="element-5659">Datos de uso:</strong> Mensajes enviados al agente,
            herramientas utilizadas, timestamps y patrones de interacción.
          </li>
          <li data-id="element-5660">
            <strong data-id="element-5661">Datos de integraciones:</strong> Contenido de emails,
            eventos de calendario y archivos procesados a través de nuestro MCP
            Gateway cuando usted lo solicita explícitamente.
          </li>
          <li data-id="element-5662">
            <strong data-id="element-5663">Datos técnicos:</strong> Dirección IP, tipo de navegador e
            información del dispositivo (utilizados exclusivamente para
            diagnóstico y seguridad).
          </li>
        </ul>
      </div>
}, {
  id: 'uso',
  title: '2. Cómo Usamos su Información',
  icon: Eye,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5664">
        <p data-id="element-5665">
          Utilizamos la información recopilada para los siguientes propósitos:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-5666">
          <li data-id="element-5667">Proveer, mantener y mejorar el Servicio.</li>
          <li data-id="element-5668">
            Procesar sus consultas y ejecutar acciones a través del agente de
            IA.
          </li>
          <li data-id="element-5669">
            Generar métricas de uso y procesar la facturación correspondiente.
          </li>
        </ul>
        <div className="p-4 bg-indigo-950/30 border border-indigo-900/50 rounded-lg mt-4" data-id="element-5670">
          <p className="font-medium text-indigo-300" data-id="element-5671">
            <strong data-id="element-5672">Importante:</strong> No vendemos sus datos personales a
            terceros bajo ninguna circunstancia, y no utilizamos sus datos
            privados para entrenar nuestros modelos de IA fundacionales.
          </p>
        </div>
      </div>
}, {
  id: 'integraciones',
  title: '3. Integraciones y Flujo de Datos',
  icon: Globe,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5673">
        <p data-id="element-5674">
          Nuestro Servicio se conecta con herramientas externas de la siguiente
          manera:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-5675">
          <li data-id="element-5676">
            <strong data-id="element-5677">Google Workspace:</strong> La autenticación se realiza vía
            OAuth a través de nuestro MCP Gateway. Los datos fluyen de servidor
            a servidor.
          </li>
          <li data-id="element-5678">
            <strong data-id="element-5679">Jira y Confluence:</strong> Conexión a través de MCP
            utilizando autenticación Cognito.
          </li>
          <li data-id="element-5680">
            <strong data-id="element-5681">GitHub:</strong> Los tokens de acceso personal se almacenan
            cifrados localmente en su navegador (localStorage).
          </li>
          <li data-id="element-5682">
            <strong data-id="element-5683">Slack:</strong> Integración mediante webhooks y polling; los
            mensajes se procesan en tiempo real.
          </li>
        </ul>
        <p data-id="element-5684">
          Los datos provenientes de estas integraciones se procesan en memoria
          para responder a sus consultas y no se almacenan permanentemente en
          nuestros servidores, a excepción del historial de chat cifrado.
        </p>
      </div>
}, {
  id: 'seguridad',
  title: '4. Almacenamiento y Seguridad',
  icon: Shield,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5685">
        <p data-id="element-5686">
          Implementamos medidas de seguridad de nivel empresarial para proteger
          su información:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-5687">
          <li data-id="element-5688">
            <strong data-id="element-5689">Infraestructura:</strong> Alojada en AWS (región us-east-1)
            con certificaciones de seguridad estándar de la industria.
          </li>
          <li data-id="element-5690">
            <strong data-id="element-5691">Gestión de Tokens:</strong> Los tokens OAuth se almacenan de
            forma segura en AWS Secrets Manager y nunca se exponen en el
            navegador del usuario.
          </li>
          <li data-id="element-5692">
            <strong data-id="element-5693">Cifrado:</strong> Todas las comunicaciones en tránsito
            utilizan TLS 1.3. El historial de chat se cifra en reposo.
          </li>
          <li data-id="element-5694">
            <strong data-id="element-5695">Control de Acceso:</strong> El acceso interno a los datos
            está estrictamente restringido mediante roles IAM y el principio de
            menor privilegio.
          </li>
        </ul>
      </div>
}, {
  id: 'terceros',
  title: '5. Compartición con Terceros',
  icon: Users,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5696">
        <p data-id="element-5697">
          Solo compartimos información con terceros esenciales para el
          funcionamiento del Servicio:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-5698">
          <li data-id="element-5699">
            <strong data-id="element-5700">Proveedores de LLM (OpenAI, Anthropic):</strong> Reciben el
            contexto necesario para procesar sus consultas. Existen acuerdos
            estrictos que prohíben a estos proveedores retener sus datos o
            usarlos para entrenamiento.
          </li>
          <li data-id="element-5701">
            <strong data-id="element-5702">Proveedores de Infraestructura (AWS):</strong> Para el
            alojamiento y procesamiento seguro en la nube.
          </li>
        </ul>
        <p data-id="element-5703">
          No compartimos, vendemos ni alquilamos sus datos a anunciantes, redes
          publicitarias ni data brokers.
        </p>
      </div>
}, {
  id: 'cookies',
  title: '6. Cookies y Almacenamiento Local',
  icon: Database,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5704">
        <p data-id="element-5705">
          Nuestro enfoque respecto al almacenamiento local respeta su
          privacidad:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-5706">
          <li data-id="element-5707">
            <strong data-id="element-5708">Sin Tracking:</strong> No utilizamos cookies de seguimiento
            (tracking cookies) ni píxeles invasivos.
          </li>
          <li data-id="element-5709">
            <strong data-id="element-5710">Uso Funcional:</strong> Utilizamos <code data-id="element-5711">localStorage</code>{' '}
            exclusivamente para mantener su sesión activa, guardar preferencias
            de interfaz y almacenar identificadores necesarios (como el{' '}
            <code data-id="element-5712">tenant_id</code> de MCP).
          </li>
          <li data-id="element-5713">
            <strong data-id="element-5714">Analytics:</strong> No integramos herramientas de analítica
            de terceros que rastreen su comportamiento individual a través de la
            web.
          </li>
        </ul>
      </div>
}, {
  id: 'derechos',
  title: '7. Sus Derechos',
  icon: UserCheck,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5715">
        <p data-id="element-5716">Usted tiene control total sobre sus datos personales:</p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-5717">
          <li data-id="element-5718">
            <strong data-id="element-5719">Acceso:</strong> Puede solicitar una copia de todos los
            datos personales que tenemos sobre usted.
          </li>
          <li data-id="element-5720">
            <strong data-id="element-5721">Rectificación:</strong> Puede corregir datos inexactos o
            incompletos en su perfil.
          </li>
          <li data-id="element-5722">
            <strong data-id="element-5723">Eliminación (Derecho al olvido):</strong> Puede solicitar el
            borrado completo de su cuenta y todos los datos asociados.
          </li>
          <li data-id="element-5724">
            <strong data-id="element-5725">Portabilidad:</strong> Puede exportar su historial y datos
            en un formato estándar y legible por máquina.
          </li>
          <li data-id="element-5726">
            <strong data-id="element-5727">Revocación:</strong> Puede desconectar cualquier integración
            (Google, Jira, etc.) en cualquier momento desde el panel de
            Conectores, lo que eliminará inmediatamente nuestro acceso.
          </li>
        </ul>
      </div>
}, {
  id: 'retencion',
  title: '8. Retención de Datos',
  icon: Clock,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5728">
        <p data-id="element-5729">Mantenemos sus datos solo durante el tiempo necesario:</p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-5730">
          <li data-id="element-5731">
            <strong data-id="element-5732">Datos de cuenta:</strong> Se conservan mientras su cuenta
            permanezca activa.
          </li>
          <li data-id="element-5733">
            <strong data-id="element-5734">Historial de chat:</strong> Se conserva hasta que usted
            decida eliminarlo manualmente desde la interfaz.
          </li>
          <li data-id="element-5735">
            <strong data-id="element-5736">Tokens OAuth:</strong> Se conservan hasta que usted
            desconecte el servicio o el token expire/sea revocado desde el
            proveedor.
          </li>
          <li data-id="element-5737">
            <strong data-id="element-5738">Logs de uso y diagnóstico:</strong> Se retienen por un
            máximo de 90 días por motivos de seguridad y depuración, luego se
            eliminan automáticamente.
          </li>
        </ul>
      </div>
}, {
  id: 'menores',
  title: '9. Menores de Edad',
  icon: AlertTriangle,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5739">
        <p data-id="element-5740">
          Nuestro Servicio está diseñado para uso profesional y empresarial, y
          no está dirigido a personas menores de 18 años.
        </p>
        <p data-id="element-5741">
          No recopilamos intencionalmente información personal de menores de
          edad. Si descubrimos que hemos recopilado datos de un menor, tomaremos
          medidas para eliminar esa información lo antes posible.
        </p>
      </div>
}, {
  id: 'cambios',
  title: '10. Cambios a esta Política',
  icon: BookOpen,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5742">
        <p data-id="element-5743">
          Podemos actualizar nuestra Política de Privacidad periódicamente para
          reflejar cambios en nuestras prácticas o por razones legales y
          regulatorias.
        </p>
        <p data-id="element-5744">
          En caso de cambios materiales, le notificaremos por correo electrónico
          o mediante un aviso destacado en la plataforma con al menos 30 días de
          anticipación. El uso continuo del Servicio después de dichos cambios
          implica su aceptación de la política actualizada.
        </p>
      </div>
}, {
  id: 'contacto',
  title: '11. Contacto del DPO',
  icon: Mail,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-5745">
        <p data-id="element-5746">
          Si tiene preguntas, inquietudes o solicitudes relacionadas con esta
          Política de Privacidad o el manejo de sus datos, puede contactar a
          nuestro Oficial de Protección de Datos (DPO):
        </p>
        <div className="bg-slate-950 p-4 rounded-lg border border-slate-800 inline-block" data-id="element-5747">
          <p className="font-mono text-indigo-400" data-id="element-5748">privacy@agent.ai</p>
        </div>
      </div>
}];
export function PrivacyPolicyPanel() {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    informacion: true
  });
  const toggleSection = (id: string) => {
    setOpenSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const expandAll = () => {
    const allOpen = PRIVACY_SECTIONS.reduce((acc, section) => {
      acc[section.id] = true;
      return acc;
    }, {} as Record<string, boolean>);
    setOpenSections(allOpen);
  };
  const collapseAll = () => {
    setOpenSections({});
  };
  const scrollToSection = (id: string) => {
    setOpenSections(prev => ({
      ...prev,
      [id]: true
    }));
    setTimeout(() => {
      const element = document.getElementById(`privacy-section-${id}`);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };
  return <div className="h-full overflow-y-auto p-4 lg:p-8 animate-in fade-in duration-300 bg-slate-950 text-slate-200" data-id="element-5749">
      <div className="max-w-4xl mx-auto space-y-8" data-id="element-5750">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4" data-id="element-5751">
          <div data-id="element-5752">
            <div className="flex items-center gap-3 mb-2" data-id="element-5753">
              <div className="p-2.5 bg-indigo-950/50 rounded-xl border border-indigo-900/50" data-id="element-5754">
                <Shield size={24} className="text-indigo-400" data-id="element-5755" />
              </div>
              <h1 className="text-3xl font-bold text-slate-100" data-id="element-5756">
                Política de Privacidad
              </h1>
            </div>
            <p className="text-slate-400 mt-2" data-id="element-5757">
              Cómo recopilamos, usamos y protegemos su información personal.
            </p>
            <div className="flex items-center gap-3 mt-4" data-id="element-5758">
              <Badge variant="outline" className="bg-slate-900 border-slate-700 text-slate-300" data-id="element-5759">
                Versión 1.3
              </Badge>
              <span className="text-xs text-slate-500" data-id="element-5760">
                Última actualización: 15 de Octubre, 2026
              </span>
            </div>
          </div>
          <div className="shrink-0" data-id="element-5761">
            <Button variant="outline" className="gap-2 bg-slate-900 border-slate-700 text-slate-300 hover:text-slate-100 hover:bg-slate-800" data-id="element-5762">
              <Download size={16} data-id="element-5763" />
              Descargar PDF
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8" data-id="element-5764">
          {/* Table of Contents (Sidebar) */}
          <div className="lg:col-span-1" data-id="element-5765">
            <div className="sticky top-8 space-y-4" data-id="element-5766">
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4" data-id="element-5767">
                Contenido
              </h3>
              <nav className="space-y-1 border-l-2 border-slate-800" data-id="element-5768">
                {PRIVACY_SECTIONS.map(section => <button key={`toc-${section.id}`} onClick={() => scrollToSection(section.id)} className={`w-full text-left px-4 py-2 text-sm transition-colors border-l-2 -ml-[2px] ${openSections[section.id] ? 'border-indigo-500 text-indigo-400 font-medium bg-indigo-950/10' : 'border-transparent text-slate-500 hover:text-slate-300 hover:border-slate-600'}`} data-id="element-5769">
                    {section.title}
                  </button>)}
              </nav>

              <div className="pt-4 flex gap-2" data-id="element-5770">
                <button onClick={expandAll} className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors" data-id="element-5771">
                  Expandir todo
                </button>
                <span className="text-slate-700" data-id="element-5772">•</span>
                <button onClick={collapseAll} className="text-xs text-slate-500 hover:text-slate-300 transition-colors" data-id="element-5773">
                  Colapsar todo
                </button>
              </div>
            </div>
          </div>

          {/* Accordion Content */}
          <div className="lg:col-span-3 space-y-4 pb-20" data-id="element-5774">
            {PRIVACY_SECTIONS.map(section => {
            const Icon = section.icon;
            const isOpen = !!openSections[section.id];
            return <Card key={section.id} id={`privacy-section-${section.id}`} className={`bg-slate-900 border-slate-800 overflow-hidden transition-colors ${isOpen ? 'border-slate-700 shadow-md' : 'hover:border-slate-700'}`} data-id="element-5775">
                  <button onClick={() => toggleSection(section.id)} className="w-full flex items-center justify-between p-5 text-left focus:outline-none" data-id="element-5776">
                    <div className="flex items-center gap-3" data-id="element-5777">
                      <div className={`p-2 rounded-lg transition-colors ${isOpen ? 'bg-indigo-950/50 text-indigo-400' : 'bg-slate-800 text-slate-400'}`} data-id="element-5778">
                        <Icon size={18} data-id="element-5779" />
                      </div>
                      <h2 className={`text-lg font-semibold transition-colors ${isOpen ? 'text-slate-100' : 'text-slate-300'}`} data-id="element-5780">
                        {section.title}
                      </h2>
                    </div>
                    <ChevronDown size={20} className={`text-slate-500 transition-transform duration-300 ${isOpen ? 'rotate-180 text-indigo-400' : ''}`} data-id="element-5781" />
                  </button>

                  <AnimatePresence initial={false} data-id="element-5782">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3,
                  ease: 'easeInOut'
                }} data-id="element-5783">
                        <div className="px-5 pb-5 pt-2 border-t border-slate-800/50" data-id="element-5784">
                          {section.content}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </Card>;
          })}
          </div>
        </div>
      </div>
    </div>;
}